/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import { AddOnAttributeDto, InputAttributeType, ProductAddOnDto } from './types';
import { Field } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { Localizer } from 'services/Localizer';
import { FieldArray } from 'react-final-form-arrays';
import { FormApi } from 'final-form';
import { TextAreaField, TextBoxField, ValidationMessages } from 'shared/edit/fields';
import {
	getCustomMultilineAttributeComponent,
	getCustomMultipleValueAttributeComponent,
	getCustomProductAddOnsComponent,
	getCustomSingleLineAttributeComponent,
} from './strategy';
import classnames from 'classnames';
import FieldLabel from 'shared/edit/fields/FieldLabel';
// #region HCL
// import ProductPricing from 'shared/components/commerce/ProductPricing';
// #endregion
import CheckboxInputField from 'shared/edit/fields/CheckboxInputField';
import InfoSVGAlternative from 'shared/components/svg/InfoSVGAlternative';
import { AxiosError } from 'axios';

export interface AddOnsFormValues {
	addOns: ProductAddOnDto[];

	/**
	 * The possible network error that occurred when submitting the form. This is stored in the form so it can be synchronously set
	 * and then used by the `onSubmit` function as part of the form values passed into it, so that it can handle them and set the correct
	 * validation state on the form.
	 */
	axiosError?: AxiosError;
}

/**
 * Product Add Ons Component props
 */
export interface ProductAddOnsProps {
	/**
	 * The list of product add Ons
	 */
	addOns: ProductAddOnDto[];

	/**
	 * The names of add-ons available for child products, if this is a group product
	 */
	childAddOnNames: string[];

	/**
	 * The product add ons formApi
	 */
	formApi: FormApi<AddOnsFormValues>;

	/**
	 * Updates product add ons
	 */
	setProductAddOns: (addOns: ProductAddOnDto[]) => void;

	/**
	 * Defines if we need to hide the add ons details (attributes)
	 * Used for group products
	 */
	hideAddOnsDetails?: boolean;
}

/**
 * Single product add on component props
 */
interface ProductAddOnFieldProps {
	/**
	 * The product add on field name
	 */
	fieldName: string;
	/**
	 * The index of the current ad on
	 */
	index: number;
	/**
	 * The product add ons formApi
	 */
	formApi: FormApi<AddOnsFormValues>;
}

/**
 * Product Add Ons Component
 */
export default function DefaultProductAddOnsComponent(props: ProductAddOnsProps) {
	const { formApi, childAddOnNames, setProductAddOns, hideAddOnsDetails } = props;

	if (!formApi) {
		return null;
	}

	return hideAddOnsDetails ? (
		<FieldArray name="addOns">
			{({ fields }) => {
				const availableAddOns: string[] = [];
				const availableChildAddOns: string[] = [];

				fields.forEach((_fieldName, index) =>
					availableAddOns.push(Localizer('PDPAddOnAvailableForItemMessage', fields.value[index].label)),
				);
				childAddOnNames.forEach((_childName, index) =>
					availableChildAddOns.push(
						Localizer('PDPAddOnAvailableForSomeItemsMessage', childAddOnNames[index]),
					),
				);

				return availableAddOns.length + childAddOnNames.length == 0 ? null : (
					<div className="roc-icon-text">
						<div className="roc-icon-text__icon">
							<InfoSVGAlternative />
						</div>
						<div className="roc-icon-text__text" data-testid="available-add-ons-string">
							{availableAddOns.join(', ')}
							{availableAddOns.length > 0 && availableChildAddOns.length > 0 ? ', ' : null}
							{availableChildAddOns.join(', ')}
						</div>
					</div>
				);
			}}
		</FieldArray>
	) : (
		<FieldArray name="addOns">
			{({ fields }) =>
				fields.map((fieldName, index) => (
					<>
						<ProductAddOnField key={index} fieldName={fieldName} index={index} formApi={formApi} />
						<OnChange name={fieldName}>
							{() => {
								const addOns = formApi.getFieldState('addOns')?.value ?? [];
								setProductAddOns([...addOns]);
							}}
						</OnChange>
					</>
				))
			}
		</FieldArray>
	);
}

/**
 * This is the default strategy for picking out a single line attribute component. It only checks if a custom one has been defined,
 * and if that returns anything, that component is returned. Otherwise, we just return the default component.
 * If you need to customize this, change `getCustomSingleLineAttributeComponent`
 */
export function getProductAddOnsComponent(): React.ComponentType<ProductAddOnsProps> {
	const customComponent = getCustomProductAddOnsComponent();

	if (customComponent !== undefined) {
		return customComponent;
	}

	return DefaultProductAddOnsComponent;
}

/**
 * Displays a single add on
 */
export function ProductAddOnField(props: ProductAddOnFieldProps) {
	const { fieldName, index, formApi } = props;

	return (
		<Field name={fieldName}>
			{({ input: { value } }: { input: { value: ProductAddOnDto } }) => (
				<div key={index} data-testid={`product-add-on-${index}`}>
					<div className="roc-panel roc-panel--slim">
						<div className="roc-panel__section">
							<div className="roc-checkbox-simple roc-accordion__header roc-accordion__header--align-top">
								<div>
									{/* #region HCL - do not show checked checkbox if add on is required */}
									{!value.required ? (
										<Field name={`${fieldName}.selected`}>
											{({ input, meta }) => (
												<div data-testid={`product-add-on-${index}-checkbox`}>
													<CheckboxInputField
														defaultValue={value.selected}
														disabled={value.required && value.selected}
														name={input.name}
														input={input}
														meta={meta}
														label={`${Localizer('ProductAddOnCheckboxLabel')} ${
															value.label
														}`}
													/>
												</div>
											)}
										</Field>
									) : null}
									{/* #endregion*/}

									{value.description ? (
										<p>
											<span dangerouslySetInnerHTML={{ __html: value.description }} />
										</p>
									) : null}
								</div>

								<span data-testid={`product-add-on-${index}-pricing`}>
									{/* #region HCL - do not show add-on price */}
									{/*<ProductPricing
										displayPrice={value.unitPrice}
										className="roc-pdp-addon-price"
										inline
										testId="pdp-main-price"
									/>*/}
									{/*// #endregion*/}
								</span>
							</div>

							<div className="roc-accordion__content" data-testid={`product-add-on-${index}-attributes`}>
								{value.selected && value.attributes && value.attributes.length > 0 ? (
									<>
										{/* #region HCL*/}
										{index === 0 && <p>{Localizer('RequiredFields')}</p>}
										{/* #endregion*/}
										<FieldArray name={`${fieldName}.attributes`}>
											{({ fields }) =>
												fields.map((fieldName, index) => (
													<Field name={fieldName} key={index}>
														{({
															input: { value },
															meta,
														}: {
															input: { value: AddOnAttributeDto };
															meta;
														}) => (
															<>
																<ProductAddOnAttributeField
																	fieldName={fieldName}
																	attributeIndex={index}
																	formApi={formApi}
																	attribute={value}
																	addOnIndex={props.index}
																/>
																<ValidationMessages
																	id={fieldName + '-invalid'}
																	meta={meta}
																/>
															</>
														)}
													</Field>
												))
											}
										</FieldArray>
									</>
								) : null}
							</div>
						</div>
					</div>
				</div>
			)}
		</Field>
	);
}

/**
 * Single product add on attribute component props
 */
export interface ProductAddOnAttributeFieldProps {
	/**
	 * The add on attribute fieldname
	 */
	fieldName: string;
	/**
	 * The add on attribute index
	 */
	attributeIndex: number;
	/**
	 * The add ons formapi
	 */
	formApi: FormApi<AddOnsFormValues>;
	/**
	 * The attribute data
	 */
	attribute: AddOnAttributeDto;
	/**
	 * The add on index
	 */
	addOnIndex: number;
}

/**
 * Single product add on attribute component
 */
function ProductAddOnAttributeField(props: ProductAddOnAttributeFieldProps) {
	const { fieldName, attributeIndex, formApi, attribute, addOnIndex } = props;

	if (!attribute) {
		return null;
	}

	if (attribute.inputAttributeType == InputAttributeType.Single) {
		const SingleLineAttributeComponent = getSingleLineAttributeComponent();

		return (
			<SingleLineAttributeComponent
				addOnIndex={addOnIndex}
				attribute={attribute}
				fieldName={fieldName}
				attributeIndex={attributeIndex}
				formApi={formApi}
			/>
		);
	}

	if (attribute.inputAttributeType == InputAttributeType.Multiline) {
		const MultiLineAttributeComponent = getMultilineAttributeComponent();

		return (
			<MultiLineAttributeComponent
				addOnIndex={addOnIndex}
				attribute={attribute}
				fieldName={fieldName}
				attributeIndex={attributeIndex}
				formApi={formApi}
			/>
		);
	}

	const MultiValueAttributeComponent = getMultiValueAttributeComponent();

	return (
		<MultiValueAttributeComponent
			addOnIndex={addOnIndex}
			attribute={attribute}
			fieldName={fieldName}
			attributeIndex={attributeIndex}
			formApi={formApi}
		/>
	);
}

/**
 * Attribute value length constraint message component props
 */
interface ProductAddOnAttributeValueLengthProps {
	/**
	 * The minimum allowed value length
	 */
	minimumLength?: number | null;
	/**
	 * The maximum allowed value length
	 */
	maximumLength?: number | null;
	/**
	 * The maximum allowed number of lines
	 */
	maximumLines?: number | null;
}

function AttributeValueLengthMessage(props: ProductAddOnAttributeValueLengthProps) {
	const { minimumLength, maximumLength, maximumLines } = props;
	return (
		<div>
			{minimumLength && maximumLength && (
				<p>{Localizer('MinimumAndMaximumLengthMessage', minimumLength.toString(), maximumLength.toString())}</p>
			)}
			{minimumLength && !maximumLength && <p>{Localizer('MinimumLengthMessage', minimumLength.toString())}</p>}
			{!minimumLength && maximumLength && <p>{Localizer('MaximumLengthMessage', maximumLength.toString())}</p>}
			{maximumLines && <p>{Localizer('MaximumLinesMessage', maximumLines.toString())}</p>}
		</div>
	);
}

/**
 * Default rendering for an individual Single Line attribute
 */
function DefaultSingleLineAttributeComponent(props: ProductAddOnAttributeFieldProps) {
	const { attributeIndex, attribute, fieldName, addOnIndex } = props;
	return (
		<Field name={fieldName}>
			{({}) => (
				<>
					<div
						key={attributeIndex}
						className="roc-pdp-selections__item"
						data-testid={`product-add-on-${addOnIndex}-attribute-${attributeIndex}`}
					>
						<FieldLabel
							label={attribute.label}
							isRequired={attribute.required}
							name={`${fieldName}.data`}
						/>
						{attribute.instructions && <span>{attribute.instructions}</span>}
						<Field name={`${fieldName}.data`}>
							{({}) => (
								<>
									<TextBoxField
										name={`${fieldName}.data`}
										testId={`product-add-on-${addOnIndex}-attribute-${attributeIndex}-textBox`}
									/>
									{/* #region HCL -do not show the built-in field description */}
									{/*<AttributeValueLengthMessage
										minimumLength={attribute.minimumLength}
										maximumLength={attribute.maximumLength}
										maximumLines={attribute.maximumNumberOfLines}
									/>*/}
									{/* #endregion*/}
								</>
							)}
						</Field>
					</div>
				</>
			)}
		</Field>
	);
}

/**
 * This is the default strategy for picking out a single line attribute component. It only checks if a custom one has been defined,
 * and if that returns anything, that component is returned. Otherwise, we just return the default component.
 * If you need to customize this, change `getCustomSingleLineAttributeComponent`
 */
function getSingleLineAttributeComponent(): React.ComponentType<ProductAddOnAttributeFieldProps> {
	const customComponent = getCustomSingleLineAttributeComponent();

	if (customComponent !== undefined) {
		return customComponent;
	}

	return DefaultSingleLineAttributeComponent;
}

/**
 * Default rendering for an individual Multiline attribute
 */
function DefaultMultiLineAttributeComponent(props: ProductAddOnAttributeFieldProps) {
	const { attributeIndex, attribute, fieldName, addOnIndex } = props;

	/**
	 * Limit the number of lines if multiline max given.
	 *
	 * @param {*} value
	 * @return {*}
	 */
	const limitMultiline = (value) => {
		if (attribute.maximumNumberOfLines) {
			const newLines = value.split('\n').length - 1;
			const lastChar = value[value.length - 1];

			if (lastChar === '\n' && newLines >= attribute.maximumNumberOfLines) {
				return value.substring(0, value.length - 1);
			}

			return value;
		}

		return value;
	};

	return (
		<Field name={fieldName}>
			{({ input: { name } }) => (
				<div
					key={attributeIndex}
					className="roc-pdp-selections__item"
					data-testid={`product-add-on-${addOnIndex}-attribute-${attributeIndex}`}
				>
					<FieldLabel name={`${name}.data`} isRequired={attribute.required} label={attribute.label} />
					{attribute.instructions && <span>{attribute.instructions}</span>}

					<div className="roc-pdp-review-form__body">
						<TextAreaField
							parse={limitMultiline}
							rowLimit={attribute.maximumNumberOfLines ?? undefined}
							rows={attribute.maximumNumberOfLines ?? undefined}
							multiple={true}
							name={`${name}.data`}
							maxLength={attribute.maximumLength ?? undefined}
							testId={`product-add-on-${addOnIndex}-attribute-${attributeIndex}-textArea`}
							isRequired={attribute.required}
						/>
						<AttributeValueLengthMessage
							minimumLength={attribute.minimumLength}
							maximumLength={attribute.maximumLength}
							maximumLines={attribute.maximumNumberOfLines}
						/>
					</div>
				</div>
			)}
		</Field>
	);
}

/**
 * This is the default strategy for picking out a multiline attribute component. It only checks if a custom one has been defined,
 * and if that returns anything, that component is returned. Otherwise, we just return the default component.
 * If you need to customize this, change `getCustomMultipleLineAttributeComponent`
 */
function getMultilineAttributeComponent(): React.ComponentType<ProductAddOnAttributeFieldProps> {
	const customComponent = getCustomMultilineAttributeComponent();

	if (customComponent !== undefined) {
		return customComponent;
	}

	return DefaultMultiLineAttributeComponent;
}

/**
 * Default rendering for an individual selection attribute
 */
function DefaultMultiValueAttributeComponent(props: ProductAddOnAttributeFieldProps) {
	const { attributeIndex, attribute, fieldName, addOnIndex, formApi } = props;

	const resetSelectedValues = () => {
		formApi.batch(() => {
			// Set `selected` to false for all attributes
			for (let i = 0; i < attribute.values.length; i++) {
				formApi.change<any>(`${fieldName}.values[${i}].selected`, false);
			}
		});
	};

	return (
		<div
			key={attributeIndex}
			className="roc-pdp-selections__item"
			data-testid={`product-add-on-${addOnIndex}-attribute-${attributeIndex}`}
		>
			<div className="roc-inline-link-list roc-inline-link-list--flush">
				<FieldLabel label={attribute.label} isRequired={attribute.required} name={`${fieldName}.values`} />
				{attribute.required ? null : (
					<button
						type="button"
						className="roc-inline-link-list__item roc-btn-link"
						onClick={resetSelectedValues}
					>
						{Localizer('ClearPDPAddOnSelection')}
					</button>
				)}
			</div>

			{attribute.instructions ? <span>{attribute.instructions}</span> : null}

			<Field name={`${fieldName}.values`}>
				{({ meta }) => (
					<>
						<div
							className="roc-pdp-selections__item-content"
							data-testid={`product-add-on-${addOnIndex}-attribute-${attributeIndex}-values`}
						>
							{attribute.values
								? attribute.values.map((selectionValue, valueIndex) => (
										<label
											htmlFor={`${fieldName}.values[${valueIndex}].selected`}
											key={valueIndex}
											className={classnames('roc-pdp-selections__item-label', {
												'roc-pdp-selections__item-label--active': selectionValue.selected,
											})}
											data-testid={`product-add-on-${addOnIndex}-attribute-${attributeIndex}-value-${valueIndex}-label`}
										>
											{selectionValue.label}
											<Field name={`${fieldName}.values[${valueIndex}].selected`} type="radio">
												{({ input: { name, onChange } }) => (
													<input
														className="roc-pdp-selections__item-input roc-visually-hidden"
														id={name}
														name={name}
														checked={selectionValue.selected}
														type="radio"
														value={selectionValue.value}
														onChange={onChange}
														data-testid={`product-add-on-${addOnIndex}-attribute-${attributeIndex}-value-${valueIndex}-radio`}
													/>
												)}
											</Field>

											<OnChange name={`${fieldName}.values[${valueIndex}].selected`}>
												{(selected: boolean) => {
													if (selected) {
														const addOns = formApi.getFieldState('addOns')?.value;
														if (addOns) {
															const attribute =
																addOns[addOnIndex].attributes[attributeIndex];
															for (let i = 0; i < attribute.values.length; i++) {
																attribute.values[i].selected = i === valueIndex;
															}
															addOns[addOnIndex].attributes[attributeIndex] = {
																...attribute,
															};
														}

														if (addOns) {
															formApi.change('addOns', [...addOns]);
														}
													}
												}}
											</OnChange>
										</label>
								  ))
								: null}
						</div>
						<ValidationMessages id={`${fieldName}.values` + '-invalid'} meta={meta} />
					</>
				)}
			</Field>
		</div>
	);
}

/**
 * This is the default strategy for picking out a selection attribute component. It only checks if a custom one has been defined,
 * and if that returns anything, that component is returned. Otherwise, we just return the default component.
 * If you need to customize this, change `getCustomMultipleValueAttributeComponent`
 */
function getMultiValueAttributeComponent(): React.ComponentType<ProductAddOnAttributeFieldProps> {
	const customComponent = getCustomMultipleValueAttributeComponent();

	if (customComponent !== undefined) {
		return customComponent;
	}

	return DefaultMultiValueAttributeComponent;
}
